
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Search } from 'lucide-react';

const Help = () => {
  return (
    <div className="container mx-auto px-4 py-8 max-w-5xl">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-foreground">Help & Support</h1>
        <p className="text-muted-foreground">Find answers to common questions or contact our support team</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[1fr_300px] gap-8">
        <div className="space-y-8">
          {/* Search */}
          <Card>
            <CardContent className="pt-6">
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input 
                  type="text" 
                  placeholder="Search help articles..." 
                  className="pl-10"
                />
              </div>
            </CardContent>
          </Card>

          {/* Frequently Asked Questions */}
          <Card>
            <CardHeader>
              <CardTitle>Frequently Asked Questions</CardTitle>
            </CardHeader>
            <CardContent>
              <Accordion type="single" collapsible className="w-full">
                <AccordionItem value="item-1">
                  <AccordionTrigger>How do I subscribe to a journalist?</AccordionTrigger>
                  <AccordionContent>
                    <p className="mb-2">
                      To subscribe to a journalist, navigate to their profile page by clicking on their name anywhere on the platform. 
                      On their profile, you'll find a "Subscribe" button. Click it and follow the prompts to complete your subscription.
                    </p>
                    <p>
                      You can manage all your subscriptions in your account settings under "Subscriptions".
                    </p>
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="item-2">
                  <AccordionTrigger>What payment methods are accepted?</AccordionTrigger>
                  <AccordionContent>
                    <p>DirectPress accepts all major credit cards (Visa, Mastercard, American Express, Discover), as well as PayPal and Apple Pay. For select regions, we also support Google Pay and regional payment methods.</p>
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="item-3">
                  <AccordionTrigger>How do I cancel my subscription?</AccordionTrigger>
                  <AccordionContent>
                    <p>
                      You can cancel any subscription by going to your Account Settings, selecting the "Billing" tab, and clicking on "Manage Subscriptions". From there, find the subscription you wish to cancel and click the "Cancel" button.
                    </p>
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="item-4">
                  <AccordionTrigger>What is the micropayment wallet?</AccordionTrigger>
                  <AccordionContent>
                    <p className="mb-2">
                      The micropayment wallet is a feature that allows you to add funds to your account for making small payments to access individual articles without committing to a full subscription.
                    </p>
                    <p>
                      You can add funds to your wallet in the "Billing" section of your account settings. Micropayments typically range from $0.25 to $2.00 per article, depending on the journalist's pricing.
                    </p>
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="item-5">
                  <AccordionTrigger>How do I become a journalist on DirectPress?</AccordionTrigger>
                  <AccordionContent>
                    <p>
                      To become a journalist on DirectPress, click on the "I'm a Journalist" button on our homepage or go directly to our journalist onboarding page. You'll need to provide some professional information, examples of your work, and set up your profile and payment details. Our team will review your application and typically respond within 48 hours.
                    </p>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </CardContent>
          </Card>
          
          {/* Contact Support */}
          <Card>
            <CardHeader>
              <CardTitle>Contact Support</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="mb-4 text-muted-foreground">
                Can't find what you're looking for? Send us a message and our support team will get back to you as soon as possible.
              </p>
              <div className="space-y-4">
                <div>
                  <label htmlFor="subject" className="block text-sm font-medium mb-1">Subject</label>
                  <Input id="subject" placeholder="What's your inquiry about?" />
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-medium mb-1">Message</label>
                  <textarea 
                    id="message" 
                    rows={5}
                    className="w-full p-3 border border-input rounded-md focus:ring-2 focus:ring-ring focus:outline-none"
                    placeholder="Please describe your issue in detail..."
                  ></textarea>
                </div>
                <Button className="w-full sm:w-auto">Submit Request</Button>
              </div>
            </CardContent>
          </Card>
        </div>
        
        {/* Sidebar */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Support Options</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-4">
                <li>
                  <Button variant="outline" className="w-full justify-start">
                    Email Support
                  </Button>
                </li>
                <li>
                  <Button variant="outline" className="w-full justify-start">
                    Live Chat
                  </Button>
                </li>
                <li>
                  <Button variant="outline" className="w-full justify-start">
                    Knowledge Base
                  </Button>
                </li>
                <li>
                  <Button variant="outline" className="w-full justify-start">
                    Video Tutorials
                  </Button>
                </li>
              </ul>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle>Help By Topic</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                <li>
                  <a href="#" className="text-sm text-blue-600 hover:underline">Account & Security</a>
                </li>
                <li>
                  <a href="#" className="text-sm text-blue-600 hover:underline">Subscriptions & Billing</a>
                </li>
                <li>
                  <a href="#" className="text-sm text-blue-600 hover:underline">Reading Experience</a>
                </li>
                <li>
                  <a href="#" className="text-sm text-blue-600 hover:underline">Publishing Tools</a>
                </li>
                <li>
                  <a href="#" className="text-sm text-blue-600 hover:underline">Micropayments</a>
                </li>
                <li>
                  <a href="#" className="text-sm text-blue-600 hover:underline">Technical Issues</a>
                </li>
              </ul>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-sm text-muted-foreground mb-3">Need immediate assistance?</p>
                <p className="font-medium mb-1">Call our support line</p>
                <p className="text-lg font-bold mb-3">1-800-DIRECT-PRESS</p>
                <p className="text-xs text-muted-foreground">
                  Available Monday-Friday, 9am-5pm ET
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Help;
